import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../api/client'
import { errorCode, errorMessage } from '../api/ApiError'
import DataTable, { type Column } from '../components/DataTable'
import KpiGrid from '../components/KpiGrid'
import StatusPill from '../components/StatusPill'
import WindowChip from '../components/WindowChip'
import SlaChip from '../components/SlaChip'
import type { ConversationListResponse, ConversationSummary } from '../types/portal'
import { QUE_ES_UN_CLIENTE_ESPERANDO, esperandoFueraDePlazo } from '../sla'
import { esperaAsesor } from '../cola'

/**
 * "Cola global" — la vista del supervisor sobre lo que espera atención.
 *
 * Muestra las conversaciones SIN asignar y las que requieren asesor. Desde acá
 * se dispara el reparto automático (round-robin) o se asigna a un asesor
 * puntual. El asesor no ve esta pantalla: su bandeja solo trae lo suyo.
 */


function espera(ts: string | null): string {
  if (!ts) return '—'
  const minutos = Math.floor((Date.now() - new Date(ts).getTime()) / 60_000)
  if (minutos < 60) return `${minutos} min`
  const horas = Math.floor(minutos / 60)
  return horas < 24 ? `${horas} h` : `${Math.floor(horas / 24)} d`
}

export default function Queue() {
  const navigate = useNavigate()
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accion, setAccion] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<ConversationListResponse>('/conversations?limit=100')
      setConversations(res.data.conversations ?? [])
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'No se pudo cargar la cola.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetch() }, [fetch])

  // Todo lo que está sin dueño y pide asesor, sin importar la antigüedad.
  const enColaTodas = useMemo(
    () => conversations.filter(esperaAsesor),
    [conversations],
  )

  // La cola REAL: solo lo que tuvo actividad en las últimas 24 h.
  //
  // Sin este corte la pantalla mezclaba pruebas abandonadas hace días con
  // conversaciones del momento, y anunciaba a alguien "esperando 4 d" — un
  // número que no es un pendiente sino un residuo. Una cola donde el 70 % de
  // las filas no se va a atender nunca deja de leerse: el supervisor aprende a
  // ignorarla entera.
  const enCola = useMemo(
    () => enColaTodas.filter((c) => c.activity?.is_live ?? true),
    [enColaTodas],
  )
  const historicas = useMemo(
    () => enColaTodas.filter((c) => !(c.activity?.is_live ?? true)),
    [enColaTodas],
  )
  const [verHistoricas, setVerHistoricas] = useState(false)

  const sinVentana = useMemo(
    () => enCola.filter((c) => !c.window.can_send_free_text).length,
    [enCola],
  )
  // "Activas" cuenta actividad real, no `status`: una conversación asignada y
  // muerta hace una semana no es carga del equipo.
  const asignadas = useMemo(
    () => conversations.filter(
      (c) => c.assigned_to && c.status !== 'CLOSED' && (c.activity?.is_live ?? true),
    ).length,
    [conversations],
  )
  // Clientes esperando respuesta fuera de plazo, con la regla de todas las
  // pantallas (`sla.esperandoFueraDePlazo`, DEF-112). Pasan solos a otro
  // asesor que no haya fallado hoy; si no queda ninguno, quedan «para revisar»
  // y aparecen en Incidencias (documento de QA del 20-oct-2026).
  const vencidas = useMemo(
    () => conversations.filter(esperandoFueraDePlazo).length,
    [conversations],
  )

  const repartir = useCallback(async (convId: string) => {
    setAccion(convId)
    setAviso(null)
    try {
      const res = await apiClient.post<{ assigned_to: string }>(
        `/conversations/${convId}/assign`, { auto: true },
      )
      setAviso(`Asignada a ${res.data.assigned_to}.`)
      void fetch()
    } catch (err) {
      setAviso(
        errorCode(err) === 'sin_asesores_disponibles'
          ? 'Todos los asesores están en pausa: la conversación queda en la cola.'
          : errorMessage(err, 'No se pudo asignar la conversación.'),
      )
    } finally {
      setAccion(null)
    }
  }, [fetch])

  const columnas: Column<ConversationSummary>[] = [
    {
      key: 'cliente',
      header: 'Cliente',
      cell: (c) => <span className="font-semibold">{c.contact_name || 'Sin nombre'}</span>,
      sub: (c) => c.phone,
    },
    { key: 'estado', header: 'Estado', cell: (c) => <StatusPill status={c.status} expediente={c.expediente ?? null} /> },
    {
      key: 'espera',
      header: 'Esperando',
      cell: (c) => (
        c.sla && c.sla.level !== 'none'
          // El reloj del backend cuando hay alguien esperando de verdad: es el
          // mismo con el que el bot decide reasignar.
          ? <SlaChip sla={c.sla} atendida={c.atendida}
                     paraRevisar={Boolean(c.para_revisar)} compact />
          : <span className="font-mono text-micro text-ink-muted">
              {espera(c.last_message_ts)}
            </span>
      ),
    },
    { key: 'ventana', header: 'Ventana 24 h', cell: (c) => <WindowChip window={c.window} sesionSocio={c.sesion_socio} compact /> },
    {
      key: 'docs',
      header: 'Documentos',
      cell: (c) => c.docs_pending_review > 0
        ? <span className="badge-purple">{c.docs_pending_review} por revisar</span>
        : c.docs_processing > 0
          ? <span className="badge-blue">{c.docs_processing} en proceso</span>
          : <span className="text-ink-faint">—</span>,
    },
    {
      key: 'ultimo',
      header: 'Último mensaje',
      cell: (c) => (
        <span className="block max-w-[280px] truncate text-ink-muted">
          {c.last_message || '—'}
        </span>
      ),
    },
    {
      key: 'accion',
      header: '',
      align: 'right',
      cell: (c) => (
        <div className="flex justify-end gap-1.5">
          <button
            className="btn-soft disabled:opacity-50"
            disabled={accion === c.conversation_id}
            onClick={(e) => { e.stopPropagation(); void repartir(c.conversation_id) }}
          >
            {accion === c.conversation_id ? 'Asignando…' : 'Repartir'}
          </button>
          <button
            className="btn-row"
            onClick={(e) => { e.stopPropagation(); navigate(`/conversations/${c.conversation_id}`) }}
          >
            Abrir
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-5">
      <KpiGrid
        columns={4}
        kpis={[
          { label: 'En cola sin asignar', value: enCola.length,
            hint: 'Con actividad en las últimas 24 h',
            tone: enCola.length > 0 ? 'warn' : 'default' },
          { label: 'Sin responder fuera de plazo', value: vencidas,
            hint: `${QUE_ES_UN_CLIENTE_ESPERANDO} Se reasignan si hay otro asesor libre; si no, van a Incidencias`,
            tone: vencidas > 0 ? 'danger' : 'default' },
          { label: 'Ventana cerrada', value: sinVentana,
            hint: 'Solo se pueden reactivar con plantilla',
            tone: sinVentana > 0 ? 'danger' : 'default' },
          { label: 'Asignadas activas', value: asignadas,
            hint: 'Repartidas entre el equipo' },
        ]}
      />

      {aviso && (
        <p className="rounded-card border border-info-border bg-info-light px-4 py-3 text-body text-info">
          {aviso}
        </p>
      )}

      <DataTable
        title="Cola global"
        subtitle="Sin asignar y con actividad en las últimas 24 h"
        columns={columnas}
        rows={enCola}
        rowKey={(c) => c.conversation_id}
        loading={loading}
        error={error}
        onRetry={() => void fetch()}
        emptyTitle="La cola está vacía"
        emptyMessage={'Cada expediente nace asignado a un asesor, así que lo normal es que esté vacía. '
          + 'Acá llega lo que queda sin dueño: una derivación cuando no hay asesores en la rotación, '
          + 'lo que se devuelve con «Devolver a la cola» y los números que no están habilitados.'}
        action={<button className="btn-ghost" onClick={() => void fetch()}>Actualizar</button>}
      />

      {/* Las históricas no se borran ni se esconden: se guardan detrás de un
          clic. Siguen siendo casos sin asignar y alguien puede querer
          retomarlos —o darse cuenta de que son basura de pruebas— pero no
          pueden competir por la atención con lo que está pasando ahora. */}
      {historicas.length > 0 && (
        <div className="flex flex-col gap-3">
          <button
            className="self-start text-body text-ink-muted underline underline-offset-4 hover:text-ink"
            onClick={() => setVerHistoricas((v) => !v)}
          >
            {verHistoricas ? 'Ocultar' : 'Ver'} {historicas.length} conversación
            {historicas.length === 1 ? '' : 'es'} sin actividad hace más de 24 h
          </button>
          {verHistoricas && (
            <DataTable
              title="Históricas sin asignar"
              subtitle="Sin actividad hace más de 24 h. Fuera de la ventana de WhatsApp solo se reactivan con plantilla."
              columns={columnas}
              rows={historicas}
              rowKey={(c) => c.conversation_id}
              emptyTitle=""
              emptyMessage=""
            />
          )}
        </div>
      )}
    </div>
  )
}
